// src/modules/myvault/components/MyVaultAgent/FloatingChatWidget.tsx
//
// Floating chat widget that expands to show the MyVault Agent chat panel.

import React, { useState, useEffect } from "react";
import { Box, IconButton, Sheet } from "@mui/joy";
import CloseIcon from "@mui/icons-material/Close";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { ChatPanel } from "./ChatPanel";
import { useMyVaultAgentVisibility } from "../../hooks/useMyVaultAgentVisibility";

interface FloatingChatWidgetProps {
  workspaceId: string;
  canvasId?: string;
  allowCanvasRuntime?: boolean;
}

/**
 * Floating chat widget that appears in the lower-right corner.
 * Clicking the button expands/collapses the chat panel.
 *
 * Visibility is controlled by useMyVaultAgentVisibility - hidden when
 * running a Canvas App in runtime mode.
 */
export const FloatingChatWidget: React.FC<FloatingChatWidgetProps> = ({
  workspaceId,
  canvasId,
  allowCanvasRuntime = false,
}) => {
  const STORAGE_KEY = canvasId
    ? `myvault-chat-open-${workspaceId}-canvas-${canvasId}`
    : `myvault-chat-open-${workspaceId}`;
  const [isOpen, setIsOpen] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === "true";
    } catch {
      return false;
    }
  });

  // Persist open state to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, String(isOpen));
    } catch {
      /* ignore */
    }
  }, [isOpen, STORAGE_KEY]);
  const isVisible = useMyVaultAgentVisibility();

  // Don't render anything if not visible (e.g., in Canvas App runtime)
  if (!isVisible && !allowCanvasRuntime) {
    return null;
  }

  const toggleChat = () => {
    setIsOpen((prev) => !prev);
  };

  return (
    <>
      {/* Chat Panel */}
      {isOpen && (
        <Sheet
          variant="outlined"
          sx={{
            position: "fixed",
            bottom: 80,
            // Keep the panel below the app header and the MyVault page toolbar.
            // At shorter heights it stays pinned to this safe top edge and
            // shrinks; at taller heights it retains the existing 80px bottom
            // gap while growing to at most 700px.
            top: "max(120px, calc(100vh - 780px))",
            right: { xs: 16, sm: 24 },
            width: { xs: "calc(100vw - 32px)", sm: 520 },
            height: "min(700px, calc(100vh - 200px))",
            minHeight: 0,
            borderRadius: "lg",
            boxShadow: "lg",
            zIndex: 1300,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <ChatPanel workspaceId={workspaceId} canvasId={canvasId} />
        </Sheet>
      )}

      {/* Floating Button */}
      <Box
        sx={{
          position: "fixed",
          bottom: 24,
          right: 24,
          zIndex: 1300,
        }}
      >
        <IconButton
          onClick={toggleChat}
          variant="solid"
          color={isOpen ? "neutral" : "primary"}
          sx={{
            width: 48,
            height: 48,
            borderRadius: "50%",
            boxShadow: "md",
            transition: "all 0.2s ease-in-out",
            "&:hover": {
              transform: "scale(1.05)",
              boxShadow: "lg",
            },
          }}
        >
          {isOpen ? <CloseIcon /> : <AutoAwesomeIcon />}
        </IconButton>
      </Box>
    </>
  );
};

export default FloatingChatWidget;
